import {
  Button,
  FormField,
  SwitchField,
  TextInput,
} from '@dev-spendesk/grapes';
import type { FormikProps } from 'formik';
import { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import {
  useIntegrationStatusQuery,
  type ExpenseAccount,
} from 'modules/bookkeep';
import { CreateExpenseAccountModal } from 'modules/bookkeep/components/CreateExpenseAccountModal';
import { DeleteExpenseAccountModal } from 'modules/bookkeep/components/DeleteExpenseAccountModal';
import { UpdateExpenseAccountModal } from 'modules/bookkeep/components/UpdateExpenseAccountModal';
import { useExpenseAccountsQuery } from 'modules/bookkeep/settings/integrations/hooks/useExpenseAccountsQuery';
import { ExpenseAccountTable } from 'modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/ExpenseAccountsSection/ExpenseAccountTable';
import { type DefaultExpenseAccountFormValues } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingExpenseAccounts/hooks/form';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

type Props = Pick<
  FormikProps<DefaultExpenseAccountFormValues>,
  'values' | 'handleChange' | 'errors' | 'submitCount'
>;

export const ExpenseAccountTableAndModals = ({
  values,
  handleChange,
  errors,
  submitCount,
}: Props) => {
  const { t } = useTranslation('global');

  const expenseAccountQueryState = useExpenseAccountsQuery('');
  const expenseAccounts =
    expenseAccountQueryState.status === 'success'
      ? expenseAccountQueryState.data
      : [];

  const integrationStatusQueryState = useIntegrationStatusQuery();
  const integrationStatus = unwrapQuery(integrationStatusQueryState);

  const [isCreateExpenseAccountModalOpen, setIsCreateExpenseAccountModalOpen] =
    useState<boolean>(false);
  const [editingExpenseAccount, setEditingExpenseAccount] = useState<
    ExpenseAccount | undefined
  >(undefined);
  const [deletingExpenseAccount, setDeletingExpenseAccount] = useState<
    ExpenseAccount | undefined
  >(undefined);

  return (
    <>
      <div className="my-16">
        <ExpenseAccountTable
          expenseAccounts={expenseAccounts}
          fetchNextPage={expenseAccountQueryState.fetchNextPage}
          hasFooter={expenseAccountQueryState.hasNextPage}
          isLoading={
            expenseAccountQueryState.status === 'loading' &&
            !expenseAccountQueryState.isFetchingNextPage
          }
          isFetchingMore={expenseAccountQueryState.isFetchingNextPage}
          onDelete={
            integrationStatus
              ? (row) => setDeletingExpenseAccount(row)
              : undefined
          }
          onEdit={
            integrationStatus
              ? (row) => setEditingExpenseAccount(row)
              : undefined
          }
        />
      </div>
      {integrationStatus && (
        <>
          <Button
            className="mx-auto mt-16 flex"
            variant="secondaryNeutral"
            iconName="plus"
            iconPosition="left"
            onClick={() => setIsCreateExpenseAccountModalOpen(true)}
            text={t(
              'bookkeep.integrations.settings.expenseAccountsTable.addExpenseAccount',
            )}
          />
          <div className="separator my-12" />
          <div className="flex flex-col gap-8 rounded-4 bg-background-secondary-default px-24 py-16">
            <SwitchField
              helpText={t(
                'bookkeep.integrations.settings.defaultExpenseAccountForm.description',
              )}
              label={t(
                'bookkeep.integrations.settings.defaultExpenseAccountForm.name',
              )}
              name="useDefaultValue"
              isChecked={values.useDefaultValue}
              onChange={handleChange}
            />
            {values.useDefaultValue && (
              <FormField
                visuallyHideLabel
                label={t(
                  'bookkeep.integrations.settings.defaultExpenseAccountForm.name',
                )}
                alertMessage={
                  submitCount > 0 ? errors.defaultExpenseAccountCode : undefined
                }
              >
                <TextInput
                  name="defaultExpenseAccountCode"
                  value={values.defaultExpenseAccountCode}
                  onChange={handleChange}
                  placeholder={t(
                    'bookkeep.integrations.settings.defaultExpenseAccountForm.placeholder',
                  )}
                />
              </FormField>
            )}
          </div>
          <CreateExpenseAccountModal
            integrationStatus={integrationStatus}
            isOpen={isCreateExpenseAccountModalOpen}
            onClose={() => setIsCreateExpenseAccountModalOpen(false)}
          />
          <UpdateExpenseAccountModal
            account={editingExpenseAccount}
            integrationStatus={integrationStatus}
            isOpen={!!editingExpenseAccount}
            onClose={() => setEditingExpenseAccount(undefined)}
          />
          <DeleteExpenseAccountModal
            account={deletingExpenseAccount}
            integrationStatus={integrationStatus}
            isOpen={!!deletingExpenseAccount}
            onClose={() => setDeletingExpenseAccount(undefined)}
            showDescription={false}
          />
        </>
      )}
    </>
  );
};
